import { apiFetch } from './api';

/** "ig" = API de jeu (tous les envs) ; "external" = une seule instance, sur un seul groupe d'envs. */
export type ApiCategory = 'ig' | 'external';

/**
 * Contrôle de santé : "port" (le CICD sonde ip:port de l'allocation et suit les redémarrages) ;
 * "process" (worker sans port d'écoute : seule la boucle de redémarrage donne « Erreur »).
 */
export type ApiHealthCheck = 'port' | 'process';

export interface ApiService {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  repository: string;
  dockerImage: string;
  latestAvailable?: string;
  deployedVersions?: Record<string, string>;
  lastDeployedAt?: Record<string, string>;
  lastSyncAt?: string;
  createdAt?: string;
  createdBy?: string;
  /** env → "ip:port" (populated par ?live=true uniquement). */
  endpoints?: Record<string, string>;
  /** Absent sur un backend plus ancien : vaut alors "ig". */
  category?: ApiCategory;
  /**
   * Répartition par env canonique (dev, preprod, prod) : groupe de jeu → clé d'instance, ou
   * "_none" (NONE_INSTANCE, « Aucune » : le groupe ne reçoit pas l'API).
   * Absente, env absent ou groupe absent : le groupe reçoit la Principale.
   */
  routing?: Record<string, Record<string, string>>;
  /** Principale en tête, toujours présente sur un CICD à jour ; absente sur un CICD plus ancien. */
  instances?: ApiInstance[];
  /** Toujours présent sur un CICD à jour ("port" par défaut) ; absent : le CICD ne gère pas le réglage. */
  healthCheck?: ApiHealthCheck;
}

/**
 * Lien d'une instance avec son serveur Ptero sur un env canonique : bound (id rattaché),
 * legacy (Principale retrouvée par son image), ambiguous (plusieurs serveurs ont l'image),
 * missing (serveur rattaché disparu), broken (serveur d'une autre image), none (aucun).
 */
export type ApiLinkState = 'bound' | 'legacy' | 'ambiguous' | 'missing' | 'broken' | 'none';

/**
 * Exemplaire d'une API avec son propre serveur Ptero par groupe d'envs. "default" (Principale)
 * reprend les champs racine. Les groupes servis viennent de la répartition de l'API.
 */
export interface ApiInstance {
  key: string;
  label: string;
  isDefault: boolean;
  /** Les 4 envs (dev recopié sur beta), comme à la racine. */
  deployedVersions?: Record<string, string>;
  lastDeployedAt?: Record<string, string>;
  /** env → "ip:port" (live uniquement). */
  endpoints?: Record<string, string>;
  /** env canonique → état du lien (live uniquement ; env absent = Ptero illisible). */
  links?: Record<string, ApiLinkState>;
  /** env canonique → nom du serveur Ptero (live uniquement). */
  serverNames?: Record<string, string>;
  /** env canonique → groupes de jeu servis (live, projet avec groupes). */
  servedGroups?: Record<string, string[]>;
  createdAt?: string;
  createdBy?: string;
}

/** Rapport d'adoption à la création de la première instance : serveurs rattachés à la Principale. */
export interface ApiInstanceAdoption {
  environment: string;
  status: 'bound' | 'none' | 'ambiguous' | 'missing' | 'broken' | 'unreadable';
  serverName?: string;
  message?: string;
}

export interface ApiInstanceCreateResponse {
  api: ApiService;
  key: string;
  adoption?: ApiInstanceAdoption[];
}

export interface ApiInstanceDeleteResponse {
  api: ApiService;
  results: { environment: string; ok: boolean; serverName?: string; error?: string }[];
}

/** Serveur Ptero de l'image rattachable à une instance. */
export interface ApiInstanceCandidate {
  id: number;
  identifier: string;
  name: string;
  version?: string;
  endpoint?: string;
  /** Retiré d'une instance (détaché, ou instance supprimée sans ses serveurs). */
  retired: boolean;
  imageMatches: boolean;
}

/** Copie de appsettings.json d'une instance à l'autre : jamais le contenu, seulement les champs suffixés. */
export interface ApiCopyConfigResult {
  ok: boolean;
  changed: string[];
  message?: string;
  warning?: string;
  error?: string;
}

export interface ApiCreateInput {
  name: string;
  description?: string;
  repository: string;
  dockerImage?: string;
  category?: ApiCategory;
  /** Absent = "port". "process" demande cicd.deploy.prod (403 sinon). */
  healthCheck?: ApiHealthCheck;
}

export interface ApiUpdateInput {
  name?: string;
  description?: string;
  repository?: string;
  dockerImage?: string;
  category?: ApiCategory;
  /** Envoyé seulement s'il change : demande cicd.deploy.prod (403 sinon, rien n'est modifié). */
  healthCheck?: ApiHealthCheck;
}

export interface ApiDeployInput {
  environments: string[]; // dev / beta / preprod / prod
  version?: string;
  /** Clés d'instances ; absent = Principale. Envoyé seulement à un CICD qui gère les instances. */
  instances?: string[];
  /** API de base de données : son nom exact, retapé par l'utilisateur (sinon 409). Ignoré ailleurs et par un CICD plus ancien. */
  confirmDatabase?: string;
}

export interface ApiDeployResultEntry {
  environment: string;
  /** Clé d'instance ; absente (CICD plus ancien) = Principale. */
  instance?: string;
  ok: boolean;
  skipped?: boolean;
  /** True quand aucun serveur Ptero ne match → propose fresh deploy. */
  needsCreate?: boolean;
  newImage?: string;
  serverName?: string;
  eggUpdate?: Record<string, unknown>;
  error?: string;
  /** Deploy réussi mais quelque chose peut en annuler l'effet (ex : override dev actif). */
  warning?: string;
}

export interface CreateServerInput {
  environment: string;
  version?: string;
  memoryMb?: number;
  diskMb?: number;
  cpuPct?: number;
  /** Clé d'instance, absente = Principale. */
  instance?: string;
  /** Instance dont appsettings.json est copié sur le nouveau serveur (absente = config de l'image). */
  copyConfigFrom?: string;
  /** Suffixe ajouté au nom de base copié, avec copyConfigFrom. */
  databaseSuffix?: string;
  /** API de base de données : son nom exact, retapé par l'utilisateur (sinon 409). Ignoré ailleurs et par un CICD plus ancien. */
  confirmDatabase?: string;
}

export interface CreateServerResponse {
  ok: boolean;
  environment: string;
  instance?: string;
  newImage?: string;
  serverName?: string;
  info?: Record<string, unknown>;
  error?: string;
  /** Serveur créé, mais copie de config en échec ou installation trop longue. */
  warning?: string;
  copyConfig?: ApiCopyConfigResult;
}

export interface ApiDeployResponse {
  api: ApiService;
  results: ApiDeployResultEntry[];
}

export interface ApiReleaseInput {
  environments: string[]; // dev / beta / preprod / prod (dev et beta = même serveur)
  /**
   * Vide = dernière release GitHub (le backend resynchronise avant). Le panel envoie toujours la
   * version affichée et confirmée, pour qu'une release publiée entre-temps ne parte pas à sa place.
   */
  version?: string;
  /** Redéploie et redémarre même si l'API tourne déjà sur la version cible (rollback). */
  force?: boolean;
  /** Clés d'instances ; absent = Principale. 3 couples (instance, env) au plus par appel. */
  instances?: string[];
  /** API de base de données : son nom exact, retapé par l'utilisateur (sinon 409). Ignoré ailleurs et par un CICD plus ancien. */
  confirmDatabase?: string;
}

/** Résultat d'une Release sur un env. Les chaînes nulles sont omises, les booléens toujours présents. */
export interface ApiReleaseResultEntry {
  environment: string;
  /** Clé d'instance ; absente (CICD plus ancien) = Principale. */
  instance?: string;
  /** La version cible tourne ou démarre (upToDate compte comme ok). Provisoire tant que `verifying`. */
  ok: boolean;
  upToDate: boolean;
  /** Aucun serveur Ptero sur l'env : passer par la modale de déploiement (create-server). */
  needsCreate: boolean;
  /** Le workflow GitHub "Release" du tag n'a pas encore publié l'image : rien n'a été touché. */
  imageNotReady: boolean;
  /** Avec imageNotReady : le workflow "Release" a échoué, il faut le relancer (absent sur un CICD plus ancien). */
  imageFailed?: boolean;
  fromVersion?: string;
  toVersion?: string;
  /** État Ptero relu après le démarrage (running / starting / offline...). */
  powerState?: string;
  warning?: string;
  error?: string;
  /** Version à relancer pour revenir en arrière (image changée puis échec au redémarrage). */
  rollbackTo?: string;
  /**
   * Démarrage encore suivi par le CICD (pull de l'image, boot, puis ip:port qui doit répondre ;
   * worker sans port, contrôle de santé "process" : une marche sans redémarrage, 1 min par défaut
   * comptée depuis le premier running) :
   * l'issue réelle se lit sur `releaseVerifications`. Absent d'un CICD plus ancien.
   */
  verifying?: boolean;
  /** Secondes écoulées depuis le start. */
  waitedSeconds?: number;
  /**
   * Délai laissé au démarrage avant de conclure (prolongé pour un worker sans port vu en marche :
   * les vérifications publient l'échéance prolongée, à relire pendant l'attente de l'issue).
   */
  timeoutSeconds?: number;
}

export interface ApiReleaseResponse {
  api: ApiService;
  results: ApiReleaseResultEntry[];
}

const baseUrl = (pid: string) => `/api/projects/${pid}/apis`;
const instanceUrl = (pid: string, id: string, key?: string) =>
  `${baseUrl(pid)}/${id}/instances${key ? `/${encodeURIComponent(key)}` : ''}`;

export const apiServicesService = {
  /**
   * `live=true` : le backend croise chaque API avec listServers Ptero pour renvoyer
   * la version qui tourne réellement (cache 30 s côté backend). Recommandé côté /apis.
   */
  list: (pid: string, opts?: { live?: boolean }) =>
    apiFetch<ApiService[]>(`${baseUrl(pid)}${opts?.live ? '?live=true' : ''}`),
  get: (pid: string, id: string) => apiFetch<ApiService>(`${baseUrl(pid)}/${id}`),
  create: (pid: string, input: ApiCreateInput) =>
    apiFetch<ApiService>(baseUrl(pid), { method: 'POST', json: input }),
  update: (pid: string, id: string, input: ApiUpdateInput) =>
    apiFetch<ApiService>(`${baseUrl(pid)}/${id}`, { method: 'PATCH', json: input }),
  remove: (pid: string, id: string) =>
    apiFetch<void>(`${baseUrl(pid)}/${id}`, { method: 'DELETE' }),
  sync: (pid: string, id: string) =>
    apiFetch<ApiService>(`${baseUrl(pid)}/${id}/sync`, { method: 'POST' }),
  syncAll: (pid: string) =>
    apiFetch<{ ok: number; ko: number; errors: string[] }>(`${baseUrl(pid)}/sync`, { method: 'POST' }),
  deploy: (pid: string, id: string, input: ApiDeployInput) =>
    apiFetch<ApiDeployResponse>(`${baseUrl(pid)}/${id}/deploy`, { method: 'POST', json: input }),
  /**
   * Release : sync GitHub, deploy de la dernière release (ou de `version`) puis arrêt strict et
   * redémarrage. 200, ou 207 si un env n'est pas ok ; 409 si une Release tourne déjà, ou
   * {code: DATABASE_CONFIRM_REQUIRED} pour une API de base de données sans confirmDatabase exact
   * (deploy et createServer aussi). Un CICD plus ancien répond 404 (route absente).
   */
  release: (pid: string, id: string, input: ApiReleaseInput) =>
    apiFetch<ApiReleaseResponse>(`${baseUrl(pid)}/${id}/release`, { method: 'POST', json: input }),
  /**
   * Suivis de démarrage des Releases de l'API (en cours, ou finis depuis moins de 15 min), un par
   * (instance, env canonique : dev pour Dev + Bêta). Un CICD plus ancien répond 404 (route absente).
   */
  releaseVerifications: (pid: string, id: string) =>
    apiFetch<{ results: ApiReleaseResultEntry[] }>(`${baseUrl(pid)}/${id}/release/verifications`),
  /**
   * Les mêmes suivis pour toutes les API du projet, par apiId : lus à l'ouverture de la page pour
   * reprendre un suivi lancé ailleurs (page rechargée, autre onglet, autre membre du staff). Un
   * CICD plus ancien répond 404 (route absente).
   */
  projectReleaseVerifications: (pid: string) =>
    apiFetch<{ byApi: Record<string, ApiReleaseResultEntry[]> }>(`${baseUrl(pid)}/release/verifications`),
  /** Fresh deploy : crée un serveur Ptero pour cette API dans l'env donné. */
  createServer: (pid: string, id: string, input: CreateServerInput) =>
    apiFetch<CreateServerResponse>(`${baseUrl(pid)}/${id}/create-server`, {
      method: 'POST', json: input,
    }),

  // ---- Instances et répartition (un CICD plus ancien répond 404 : route absente) ----

  /** Répartition d'un env (beta = dev) : groupe → clé d'instance ou "_none" (Aucune) ; {} remet tout sur la Principale. */
  setRouting: (pid: string, id: string, environment: string, routing: Record<string, string>) =>
    apiFetch<{ api: ApiService }>(`${baseUrl(pid)}/${id}/routing`, {
      method: 'PUT', json: { environment, routing },
    }),
  /** 201 ; `key` déduite du libellé si absente. */
  createInstance: (pid: string, id: string, input: { label: string; key?: string }) =>
    apiFetch<ApiInstanceCreateResponse>(instanceUrl(pid, id), { method: 'POST', json: input }),
  /** Libellé seulement : la clé est immuable. */
  renameInstance: (pid: string, id: string, key: string, label: string) =>
    apiFetch<{ api: ApiService }>(instanceUrl(pid, id, key), { method: 'PATCH', json: { label } }),
  /** 200, ou 207 si un serveur n'a pas pu être supprimé (instance gardée) ; 409 si elle est répartie. */
  deleteInstance: (pid: string, id: string, key: string, deleteServers: boolean) =>
    apiFetch<ApiInstanceDeleteResponse>(`${instanceUrl(pid, id, key)}?deleteServers=${deleteServers}`, {
      method: 'DELETE',
    }),
  instanceCandidates: (pid: string, id: string, key: string, env: string) =>
    apiFetch<ApiInstanceCandidate[]>(`${instanceUrl(pid, id, key)}/candidates?env=${encodeURIComponent(env)}`),
  /** 409 {message, imageMismatch: true} si l'image diffère du template, sauf `force`. */
  attachInstance: (pid: string, id: string, key: string, input: { environment: string; serverId: number; force?: boolean }) =>
    apiFetch<{ api: ApiService }>(`${instanceUrl(pid, id, key)}/attach`, { method: 'POST', json: input }),
  detachInstance: (pid: string, id: string, key: string, environment: string) =>
    apiFetch<{ api: ApiService }>(`${instanceUrl(pid, id, key)}/detach`, { method: 'POST', json: { environment } }),
  /** Remplace appsettings.json de l'instance par celui de `fromInstance` (Principale par défaut). */
  copyInstanceConfig: (pid: string, id: string, key: string,
    input: { environment: string; fromInstance?: string; databaseSuffix?: string }) =>
    apiFetch<ApiCopyConfigResult>(`${instanceUrl(pid, id, key)}/copy-config`, { method: 'POST', json: input }),
};
